import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SDAGoBack from "./SDAGoBack";
import SDASteps from "./SDASteps";
import { SDATestModeBanner, SDATestSkipButton } from "./sdaTestMode";
import { useAuth } from "../../contexts/AuthContext";
import {
  DOCUMENT_ACKNOWLEDGEMENT_ID,
  downloadSignedDocument,
  signDocument,
} from "../../api/services";

interface Slide {
  title: string;
  body: React.ReactNode;
}

const SLIDES: Slide[] = [
  {
    title: "What you're paying for",
    body: (
      <>
        <p>
          Lighthouse is a non-refundable Security Deposit{" "}
          <strong>
            <em>Alternative.</em>
          </strong>
        </p>

        <p>
          Instead of paying a significantly higher refundable security deposit,
          you pay a <strong>smaller annual fee</strong> to Lighthouse to make
          move-in and move-out expenses more manageable.
        </p>

        <p>
          The fee you pay Lighthouse will not be returned when you move out, so
          think of it more like a waiver fee than a savings account.
        </p>
      </>
    ),
  },
  {
    title: "It renews every year",
    body: (
      <>
        <p>
          If you renew your lease,{" "}
          <strong>your Lighthouse coverage will automatically renew</strong>{" "}
          using your pre-authorized payment method on file.
        </p>
        <p>
          We'll send you several email reminders beforehand so there are no
          surprises.
        </p>
      </>
    ),
  },
  {
    title: "What happens at move-out",
    body: (
      <>
        <p>
          If you have a <strong>$0 balance</strong> at move-out, then nothing
          further is needed.
        </p>
        <p>
          If you do leave a balance, your property manager may file a claim for
          unpaid rent and/or damages, which you may be held responsible for.
        </p>
      </>
    ),
  },
  {
    title: "If a claim is filed against you",
    body: (
      <ul className="list-disc space-y-2 pl-5">
        <li>
          Lighthouse will notify you right away, and you'll have a few days to
          review the charges or dispute them with your property manager.
        </li>
        <li>
          If the claim is approved, Lighthouse pays your property manager on
          your behalf—up to your coverage limit.
        </li>
        <li>
          <strong>
            You are then responsible for repaying Lighthouse that amount.
          </strong>{" "}
          Payment plans are available.
        </li>
        <li>
          If the balance goes unpaid, it will be sent to collections and may
          appear on future background checks, which could affect your ability to
          rent in the future.
        </li>
      </ul>
    ),
  },
];

const SDAAcknowledge: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [index, setIndex] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [confirmedAt, setConfirmedAt] = useState<Date | null>(null);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);

  const isLast = index === SLIDES.length - 1;
  const slide = SLIDES[index];

  const handleNext = () => {
    if (!isLast) {
      setIndex((i) => i + 1);
    }
  };

  const handleBack = () => {
    if (index > 0) setIndex((i) => i - 1);
  };

  const handleContinue = async () => {
    if (!agreed || signing || !user) return;
    setSigning(true);
    setSignError(null);
    try {
      const fullName = `${user.firstName} ${user.lastName}`.trim();
      await signDocument(DOCUMENT_ACKNOWLEDGEMENT_ID, fullName, {
        inviteUuid: uuid,
      });
      setConfirmedAt(new Date());
    } catch (err: any) {
      console.error("Failed to record acknowledgement signature:", err);
      setSignError(
        err?.response?.data?.message ||
          err?.message ||
          t("sda.acknowledge.signError"),
      );
    } finally {
      setSigning(false);
    }
  };

  const handleProceedToNext = () => {
    navigate(`/sda/${uuid}/identity`);
  };

  const [saving, setSaving] = useState(false);
  const handleSaveCopy = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await downloadSignedDocument(DOCUMENT_ACKNOWLEDGEMENT_ID);
      // Trigger a browser download — axios returns the blob, and we
      // create an object URL on the fly so the user gets the file.
      const blob = new Blob([res.data as BlobPart], {
        type: "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `acknowledgement-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Failed to download acknowledgement PDF:", err);
    } finally {
      setSaving(false);
    }
  };

  const formatAgreementDate = (date: Date) =>
    date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  const formatAgreementTime = (date: Date) => {
    const time = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const tz = date
      .toLocaleTimeString("en-US", { timeZoneName: "short" })
      .split(" ")
      .pop();
    return `${time} ${tz ?? ""}`.trim();
  };

  return (
    <div>
      <SDAGoBack />

      <SDATestModeBanner />

      <SDASteps current={1} />

      <div className="rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-primary">
            {confirmedAt
              ? t("sda.acknowledge.headerAfter")
              : t("sda.acknowledge.headerBefore")}
          </h3>
          <p className="mt-0.5 text-sm text-muted">
            {confirmedAt
              ? t("sda.acknowledge.subAfter")
              : t("sda.acknowledge.subBefore")}
          </p>
        </div>

        {confirmedAt ? (
          <div className="px-6 py-10 sm:px-10">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
              <svg
                className="h-7 w-7 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <h4 className="mb-2 text-center text-xl font-semibold text-primary">
              {t("sda.acknowledge.recordedHeading")}
            </h4>
            <p className="mx-auto max-w-md text-center text-sm text-gray-600">
              {t("sda.acknowledge.recordedBody")}
            </p>

            <div className="mx-auto mt-6 max-w-md rounded-xl border border-success/20 bg-success/5 px-5 py-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-success">
                {t("sda.acknowledge.agreementRecorded")}
              </p>
              <p className="mt-1 text-sm font-semibold text-primary">
                {formatAgreementDate(confirmedAt)}
              </p>
              <p className="text-xs text-muted">
                {formatAgreementTime(confirmedAt)}
              </p>
            </div>

            <button
              type="button"
              className="mx-auto mt-4 flex w-full max-w-md cursor-pointer items-center justify-center gap-2 rounded-xl border border-gray-200 px-5 py-3 text-sm font-medium text-primary transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleSaveCopy}
              disabled={saving}
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                />
              </svg>
              {saving
                ? t("sda.acknowledge.saveCopyLoading")
                : t("sda.acknowledge.saveCopy")}
            </button>
            <p className="mt-2 text-center text-xs text-muted">
              {t("sda.acknowledge.saveCopyHint")}
            </p>

            <button
              type="button"
              className="mx-auto mt-6 flex w-full max-w-md items-center justify-center rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
              onClick={handleProceedToNext}
            >
              {t("sda.acknowledge.continue")}
            </button>
          </div>
        ) : (
          <>
            {/* Slide progress dots */}
            <div className="flex items-center justify-center gap-2 px-5 pt-5">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === index
                      ? "w-8 bg-primary"
                      : i < index
                        ? "w-1.5 bg-primary/40 hover:bg-primary/60"
                        : "w-1.5 bg-gray-200 hover:bg-gray-300"
                  }`}
                />
              ))}
            </div>

            {/* Slide body */}
            <div className="min-h-[280px] px-6 py-8 sm:px-10">
              <h4 className="mb-4 text-center text-xl font-semibold text-primary">
                {slide.title}
              </h4>
              <div className="mx-auto max-w-xl space-y-3 text-sm leading-relaxed text-gray-700">
                {slide.body}
              </div>
            </div>

            {/* Sign error (last slide only) */}
            {isLast && signError && (
              <div className="border-t border-gray-100 bg-red-50 px-6 py-3 text-sm text-danger">
                {signError}
              </div>
            )}

            {/* Agreement (last slide only) */}
            {isLast && (
              <div className="border-t border-gray-100 bg-gray-50 px-6 py-5">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                  />
                  <span className="text-sm text-gray-700">
                    {t("sda.acknowledge.agreement")}
                  </span>
                </label>
              </div>
            )}

            {/* Footer actions */}
            <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleBack}
                disabled={index === 0}
              >
                {t("sda.acknowledge.back")}
              </button>

              {isLast ? (
                <div className="flex items-center gap-2">
                  <SDATestSkipButton onSkip={handleProceedToNext} />
                  <button
                    type="button"
                    className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleContinue}
                    disabled={!agreed || signing}
                  >
                    {signing
                      ? t("sda.acknowledge.signing")
                      : t("sda.acknowledge.iAgree")}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
                  onClick={handleNext}
                >
                  {t("sda.acknowledge.continue")}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SDAAcknowledge;
